import * as React from "react";
import { Formik } from "formik";
import { styles } from "./styles";
import Box from "@mui/material/Box";
import Loader from "components/loader";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
// import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ImageDropzone from "components/input/ImageDropzone";
import Typography from "@mui/material/Typography";
import AnimateButton from "components/animateButton";
import {
  APIResponseModal,
  ReducersModal,
  ServiceTypeFormData,
} from "utils/modals";
import {
  FormControl,
  FormHelperText,
  // IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
} from "@mui/material";
import { AddServiceType } from "./action";
import { ServiceTypesSchema } from "utils/schema";
import { validateFile } from "modules/userManagement/action";
import { UPLOAD_FILE_TO_S3 } from "utils/services";
// import SHOW_TOAST from "utils/showToast";
import { FOLDER, FOLDER_TYPE } from "utils/constants";
import TitleDescriptionListInput from "components/input/TitleDescriptionListInput";
import ToolListInput from "components/input/ToolListInput";
import { buildServicePayload } from "utils/apiMappers";
import {
  SERVICE_CATEGORY,
  SERVICE_CATEGORY_LABEL,
  SERVICE_CATEGORY_SLUG,
  toServiceCategory,
} from "utils/serviceCategory";
import { useNavigate } from "react-router-dom";
import ROUTE_NAMES from "routes/routeNames";
import { isLocalPreviewUrl, resetFormData } from "utils/commonFunctions";

const SERVICE_TIMES = [
  "30 minutes",
  "45 minutes",
  "60 minutes",
  "60-90 min",
  "90 minutes",
  "2 Hours",
  "120 mins",
];

const CATEGORY_OPTIONS = [
  SERVICE_CATEGORY.CAR,
  SERVICE_CATEGORY.BIKE,
  SERVICE_CATEGORY.SOFA,
  SERVICE_CATEGORY.TANK,
  SERVICE_CATEGORY.CARPET,
];

const CATEGORY_TYPE_HINT: { [key in SERVICE_CATEGORY]: string } = {
  [SERVICE_CATEGORY.CAR]: "Configure the car service details.",
  [SERVICE_CATEGORY.BIKE]: "Configure the bike service details.",
  [SERVICE_CATEGORY.SOFA]: "Configure the sofa service details.",
  [SERVICE_CATEGORY.TANK]: "Configure the tank service details.",
  [SERVICE_CATEGORY.CARPET]: "Configure the carpet service details.",
};

type SectionProps = {
  index: number;
  title: string;
  hint: string;
  count?: number;
  countLabel?: string;
  children: React.ReactNode;
};

function FormSection({
  index,
  title,
  hint,
  count,
  countLabel,
  children,
}: SectionProps) {
  return (
    <Paper elevation={0} sx={styles.section}>
      <Box sx={styles.sectionHeader}>
        <Box sx={styles.sectionHeaderLeft}>
          <Box sx={styles.sectionIndex}>{index}</Box>
          <Box>
            <Typography sx={styles.sectionTitle}>{title}</Typography>
            <Typography sx={styles.sectionHint} variant="body2">
              {hint}
            </Typography>
          </Box>
        </Box>
        {typeof count === "number" ? (
          <Box sx={styles.sectionCount}>
            {count} {countLabel || (count === 1 ? "item" : "items")}
          </Box>
        ) : null}
      </Box>
      {children}
    </Paper>
  );
}

export default function AddServiceTypes() {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { category: selectedCategory } = useSelector(
    (state: ReducersModal) => state.ServiceTypeReducer,
  );

  const [imageUrl, setImageUrl] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [formResetKey, setFormResetKey] = React.useState(0);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const titleImageKeyRef = React.useRef("");
  const titleFileRef = React.useRef<File | null>(null);

  const initialCategory = toServiceCategory(selectedCategory);

  const goToListing = (category = initialCategory) => {
    const path = `${ROUTE_NAMES.MAIN_ROUTES.SERVICE_MANAGEMENT}/${
      SERVICE_CATEGORY_SLUG[toServiceCategory(category)]
    }`;
    navigate(path);
  };

  const uploadMediaList = async (items: any[] = [], folder: FOLDER_TYPE) => {
    return Promise.all(
      items.map(async (item: any) => {
        if (item?.file) {
                    const fileUrl = await UPLOAD_FILE_TO_S3(item.file, folder);
          return { ...item, image: fileUrl, file: undefined };
        }
        return item;
      }),
    );
  };

  const handleOnSubmit = async (values: any) => {
    setLoading(true);
    try {
      let imageKey = values.imageKey;
      if (titleFileRef.current) {
                imageKey = await UPLOAD_FILE_TO_S3(titleFileRef.current, FOLDER.SERVICE);
      }
      const tools = await uploadMediaList(values.tools, FOLDER.SERVICE_TOOLS);
      const requirementFromUser = await uploadMediaList(
        values.requirementFromUser,
        FOLDER.SERVICE_REQUIREMENTS,
      );
      const payload = buildServicePayload({
        ...values,
        imageKey,
        tools,
        requirementFromUser,
      });

      dispatch(
        AddServiceType(payload, (response: APIResponseModal) => {
          if (response.isSuccess) {
            goToListing(values.category);
          }
          setLoading(false);
        }),
      );
    } catch (err: any) {
      setLoading(false);
    }
  };

  const uploadImageFile = (
    file: File,
    callback: (imageKey: string, imageUrl: string) => void,
  ) => {
    validateFile(file, 2080)
      .then(() => {
        const localPreview = URL.createObjectURL(file);
        callback("", localPreview);
      })
      .catch(() => {});
  };

  const onImagePicked = (
    evt: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: Function,
  ) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    validateFile(file, 2080)
      .then(() => {
        const localPreview = URL.createObjectURL(file);
        if (isLocalPreviewUrl(imageUrl)) URL.revokeObjectURL(imageUrl);
        titleFileRef.current = file;
        titleImageKeyRef.current = "";
        setImageUrl(localPreview);
        setFieldValue("imageKey", "");
      })
      .catch(() => {});
    evt.target.value = "";
  };

  const onUploadImageClick = () => {
    fileRef.current?.click();
  };

  const getCoverImageState = () => ({
    imageUrl,
    titleImageKeyRef,
    titleFileRef,
    fileRef,
    setImageUrl,
  });

  return (
    <Box sx={styles.page}>
      <Formik<ServiceTypeFormData>
        key={formResetKey}
        onSubmit={handleOnSubmit}
        validationSchema={ServiceTypesSchema()}
        initialValues={{
          ...new ServiceTypeFormData(),
          category: initialCategory,
        }}
      >
        {({
          errors,
          handleBlur,
          handleSubmit,
          touched,
          values,
          setFieldValue,
        }) => {
          const category = toServiceCategory(values.category);
          const howItWorksCount = values.howItWorks?.length || 0;
          const benefitsCount = values.benefits?.length || 0;
          const toolsCount = values.tools?.length || 0;
          const requirementsCount = values.requirementFromUser?.length || 0;

          return (
            <form noValidate onSubmit={handleSubmit}>
              <FormSection
                index={1}
                title="Basic details"
                hint={CATEGORY_TYPE_HINT[category]}
              >
                <Box sx={styles.basicsLayout}>
                  <FormControl
                    fullWidth
                    error={Boolean(touched.imageKey && errors.imageKey)}
                  >
                    <Typography sx={styles.fieldLabel}>
                      Cover image
                    </Typography>
                    <Box sx={styles.coverPanel}>
                      <Box
                        sx={styles.coverDropzone}
                        onClick={onUploadImageClick}
                      >
                        <ImageDropzone
                          variant="cover"
                          imageUrl={imageUrl}
                          title="Upload cover"
                          hint="JPG or PNG"
                          onClick={onUploadImageClick}
                        />
                      </Box>
                    </Box>
                    <Typography sx={styles.coverMobileHint}>
                      Image will show the same in the mobile application
                    </Typography>
                    <Typography sx={styles.coverCaption}>
                      Preview size 163 × 229 pixels.
                    </Typography>
                    <input
                      id="fileInput"
                      type="file"
                      ref={fileRef}
                      accept=".jpg,.jpeg,.png"
                      style={{ display: "none" }}
                      onChange={(e) => onImagePicked(e, setFieldValue)}
                    />
                    {touched.imageKey && errors.imageKey && (
                      <FormHelperText error>{errors.imageKey}</FormHelperText>
                    )}
                  </FormControl>

                  <Box sx={styles.basicsFields}>
                    <Box sx={styles.basicsRow}>
                      <FormControl
                        fullWidth
                        error={Boolean(touched.category && errors.category)}
                      >
                        <InputLabel>Category</InputLabel>
                        <Select
                          sx={styles.selectDropdown}
                          name="category"
                          variant="outlined"
                          label="Category"
                          value={values.category}
                          onChange={(e: any) => {
                            const nextCategory = toServiceCategory(
                              e.target.value,
                            );
                            setFieldValue("category", nextCategory);
                          }}
                        >
                          {CATEGORY_OPTIONS.map((item) => (
                            <MenuItem key={item} value={item}>
                              {SERVICE_CATEGORY_LABEL[item]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl
                        fullWidth
                        error={Boolean(
                          touched.serviceTime && errors.serviceTime,
                        )}
                      >
                        <InputLabel>Service Time</InputLabel>
                        <Select
                          sx={styles.selectDropdown}
                          name="serviceTime"
                          variant="outlined"
                          label="Service Time"
                          value={values.serviceTime}
                          onChange={(e: any) => {
                            setFieldValue("serviceTime", e.target.value);
                          }}
                        >
                          {SERVICE_TIMES.map((item) => (
                            <MenuItem key={item} value={item}>
                              {item}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.serviceTime && errors.serviceTime && (
                          <FormHelperText error>
                            {errors.serviceTime}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Box>

                    <FormControl
                      fullWidth
                      error={Boolean(touched.title && errors.title)}
                    >
                      <InputLabel htmlFor="service-name">
                        Service Name
                      </InputLabel>
                      <OutlinedInput
                        fullWidth
                        required
                        type="text"
                        name="title"
                        label="Service Name"
                        id="service-name"
                        onBlur={handleBlur}
                        onChange={({ target }: any) => {
                          setFieldValue("title", target.value.trimStart());
                        }}
                        value={values.title}
                        placeholder="Enter service name"
                        inputProps={{ maxLength: 150 }}
                        sx={styles.selectDropdown}
                      />
                      {touched.title && errors.title && (
                        <FormHelperText error>{errors.title}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                </Box>
              </FormSection>

              <FormSection
                index={2}
                title="How it works"
                hint="Break the service into clear, ordered steps."
                count={howItWorksCount}
                countLabel={howItWorksCount === 1 ? "step" : "steps"}
              >
                <TitleDescriptionListInput
                  label=""
                  addButtonLabel="Add step"
                  titlePlaceholder="e.g. High Pressure Wash"
                  descriptionPlaceholder="Describe what happens in this step"
                  emptyTitle="No steps added"
                  emptyHint="Add the first step customers should understand about this service."
                  value={values.howItWorks}
                  error={
                    touched.howItWorks && typeof errors.howItWorks === "string"
                      ? errors.howItWorks
                      : undefined
                  }
                  onChange={(next) => {
                    setFieldValue("howItWorks", next);
                  }}
                />
              </FormSection>

              <FormSection
                index={3}
                title="Benefits"
                hint="Highlight why customers should choose this service."
                count={benefitsCount}
                countLabel={benefitsCount === 1 ? "benefit" : "benefits"}
              >
                <TitleDescriptionListInput
                  label=""
                  addButtonLabel="Add benefit"
                  titlePlaceholder="e.g. Quick Cleaning"
                  descriptionPlaceholder="Describe this benefit"
                  emptyTitle="No benefits added"
                  emptyHint="Add the first benefit to showcase on the service page."
                  value={values.benefits}
                  error={
                    touched.benefits && typeof errors.benefits === "string"
                      ? errors.benefits
                      : undefined
                  }
                  onChange={(next) => setFieldValue("benefits", next)}
                />
              </FormSection>

              <FormSection
                index={4}
                title="Tools"
                hint="List tools used for this service with a photo and name."
                count={toolsCount}
                countLabel={toolsCount === 1 ? "tool" : "tools"}
              >
                <ToolListInput
                  hideHeader
                  label="Tools"
                  placeholder="e.g. Pressure Washer"
                  addButtonLabel="tool"
                  emptyTitle="No tools added"
                  emptyHint="Upload a tool image, enter its name, then click Add tool."
                  value={values.tools}
                  error={touched.tools ? (errors.tools as string) : undefined}
                  onChange={(next) => setFieldValue("tools", next)}
                  onUploadImage={(file, callback) =>
                    uploadImageFile(file, callback)
                  }
                />
              </FormSection>

              <FormSection
                index={5}
                title="Requirement from user"
                hint="Tell customers what they need to keep ready."
                count={requirementsCount}
                countLabel={
                  requirementsCount === 1 ? "requirement" : "requirements"
                }
              >
                <ToolListInput
                  hideHeader
                  label="Requirement From User"
                  placeholder="e.g. Bucket & water"
                  addButtonLabel="requirement"
                  emptyTitle="No requirements added"
                  emptyHint="Upload an image, enter a requirement title, then click Add requirement."
                  value={values.requirementFromUser}
                  error={
                    touched.requirementFromUser
                      ? (errors.requirementFromUser as string)
                      : undefined
                  }
                  onChange={(next) =>
                    setFieldValue("requirementFromUser", next)
                  }
                  onUploadImage={(file, callback) =>
                    uploadImageFile(file, callback)
                  }
                />
              </FormSection>

              <Box sx={styles.actions}>
                <Button
                  size="large"
                  type="button"
                  variant="outlined"
                  color="secondary"
                  onClick={() =>
                    resetFormData(values, getCoverImageState(), () =>
                      setFormResetKey((key) => key + 1)
                    )
                  }
                  sx={styles.cancelBtn}
                >
                  Reset
                </Button>
                <AnimateButton>
                  <Button
                    disableElevation
                    size="large"
                    type="submit"
                    variant="contained"
                    color="secondary"
                    sx={styles.submitBtn}
                  >
                    Add Service Type
                  </Button>
                </AnimateButton>
              </Box>
            </form>
          );
        }}
      </Formik>
      {loading && <Loader />}
    </Box>
  );
}
